import React from "react";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import SuperadminDashboard from "./pages/SuperadminDashboard";
import SuperadminTenants from "./pages/SuperadminTenants";
import SuperadminAnalytics from "./pages/SuperadminAnalytics";
import SuperadminSettings from "./pages/SuperadminSettings";
import "./index.css";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import Layout from "./components/Layout";
import Inventory from "./pages/Inventory";
import NoPageFound from "./pages/NoPageFound";
import AuthContext from "./AuthContext";
import { useNotification } from "./components/Notifications";
import ProtectedWrapper from "./ProtectedWrapper";
import { useEffect, useState, useRef } from "react";
import { apiFetch } from "./api/client";
import Store from "./pages/Store";
import ManageCategory from "./pages/ManageCategory";
import ManageUsers from "./pages/ManageUsers";
import Subscription from "./pages/Subscription";
import LandingPage from "./pages/LandingPage";
import Sales from "./pages/Sales";
import PurchaseDetails from "./pages/PurchaseDetails";
import PurchaseOrderDrafts from "./pages/PurchaseOrderDrafts";
import AiPerformance from "./pages/AiPerformance";
import ReturnedProducts from "./pages/ReturnedProduct";
import VerifyEmail from "./pages/VerifyEmail";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ChangePassword from "./pages/ChangePassword";
import Profile from "./pages/Profile";
import PWAInstallPrompt from "./components/PWAInstallPrompt";

const App = () => {
    // store full user object (or null) to avoid ambiguity between falsy empty-string and object
    const [user, setUser] = useState(null);
    const [loader, setLoader] = useState(true);
    const [subscriptionPlan, setSubscriptionPlan] = useState(null);
    const [planLoaded, setPlanLoaded] = useState(false);
    const notify = useNotification();
    const logoutTimerRef = useRef(null);
    const unauthorizedHandledRef = useRef(false);

    // On app boot, restore user session from storage
    useEffect(() => {
        let mounted = true;

        async function bootAuth() {
            try {
                const stored = localStorage.getItem("user");
                
                if (stored) {
                    try {
                        const parsed = JSON.parse(stored);
                        if (mounted) setUser(parsed);
                    } catch {
                        if (mounted) setUser("");
                    }
                } else {
                    if (mounted) setUser(null);
                }
            } catch (err) {
                console.error("bootAuth error", err);
                localStorage.removeItem("user");
                if (mounted) {
                    setUser("");
                }
            } finally {
                if (mounted) setLoader(false);
            }
        }

        bootAuth();

        return () => { mounted = false; };
    }, []);

    // Auto-logout when the JWT access token expires (client-side fallback)
    useEffect(() => {
        // clear any existing timer
        if (logoutTimerRef.current) {
            try { clearTimeout(logoutTimerRef.current); } catch (e) { }
            logoutTimerRef.current = null;
        }

        try {
            const token = user?.token || user?.Token || null;
            if (!token) return;

            // decode JWT payload to get exp (seconds since epoch)
            const parts = token.split('.');
            if (parts.length !== 3) return;
            const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
            const exp = payload?.exp;
            if (!exp) return;

            const msUntilExpiry = exp * 1000 - Date.now();
            if (msUntilExpiry <= 0) {
                // already expired - trigger unauthorized flow
                window.dispatchEvent(new CustomEvent('unauthorized', { detail: { reason: 'token_expired' } }));
                return;
            }

            // set timer to auto-logout when token expires (add small buffer)
            logoutTimerRef.current = setTimeout(() => {
                window.dispatchEvent(new CustomEvent('unauthorized', { detail: { reason: 'token_expired' } }));
            }, msUntilExpiry + 500);
        } catch (err) {
            // ignore decoding errors
        }

        return () => {
            if (logoutTimerRef.current) {
                try { clearTimeout(logoutTimerRef.current); } catch (e) { }
                logoutTimerRef.current = null;
            }
        };
    }, [user]);

    useEffect(() => {
        if (user) {
            unauthorizedHandledRef.current = false;
        }
    }, [user]);

    // Also check token expiry on visibility change and periodically as a fallback
    useEffect(() => {
        let intervalId = null;

        const checkExpiryNow = () => {
            try {
                const token = user?.token || user?.Token || null;
                if (!token) return;
                const parts = token.split('.');
                if (parts.length !== 3) return;
                const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
                const exp = payload?.exp;
                if (!exp) return;
                const msUntilExpiry = exp * 1000 - Date.now();
                if (msUntilExpiry <= 0) {
                    window.dispatchEvent(new CustomEvent('unauthorized', { detail: { reason: 'token_expired' } }));
                }
            } catch (err) {
                // ignore
            }
        };

        const onVisibility = () => {
            if (!document.hidden) {
                checkExpiryNow();
            }
        };

        document.addEventListener('visibilitychange', onVisibility);

        // periodic check every 10s while user is present
        if (user) {
            intervalId = setInterval(() => {
                checkExpiryNow();
            }, 10000);
        }

        return () => {
            document.removeEventListener('visibilitychange', onVisibility);
            if (intervalId) clearInterval(intervalId);
        };
    }, [user]);

    // Load subscription plan globally when user is authenticated
    useEffect(() => {
        let mounted = true;

        async function loadPlan() {
            try {
                // Try to use cached plan from localStorage first
                const cached = localStorage.getItem("subscriptionPlan");
                if (cached) {
                    try {
                        const cachedPlan = JSON.parse(cached);
                        const cachedExpiry = cachedPlan?.expiresAt || cachedPlan?.ExpiresAt;
                        const cachedExpired = cachedExpiry && new Date(cachedExpiry) <= new Date();
                        const cachedIsPremium = String(cachedPlan?.name || cachedPlan?.Name || '').toLowerCase().includes('premium');
                        if (mounted && !(cachedIsPremium && cachedExpired)) {
                            setSubscriptionPlan(cachedPlan);
                            setPlanLoaded(true);
                        }
                    } catch (e) {
                        // ignore parse error, will fetch fresh
                    }
                }

                // Always fetch fresh data from API
                const res = await apiFetch('/api/subscription');
                if (!res.ok) {
                    if (mounted) {
                        const basicPlan = { name: 'Basic', expiresAt: null };
                        setSubscriptionPlan(basicPlan);
                        try { localStorage.setItem("subscriptionPlan", JSON.stringify(basicPlan)); } catch (e) { }
                        setPlanLoaded(true);
                    }
                    return;
                }
                const data = await res.json().catch(() => null);
                const plan = data?.plan || data?.data?.plan || null;
                if (mounted) {
                    if (plan) {
                        const planObj = { name: plan.name || plan.Name || null, expiresAt: plan.expiresAt || plan.ExpiresAt || null };
                        setSubscriptionPlan(planObj);
                        try { localStorage.setItem("subscriptionPlan", JSON.stringify(planObj)); } catch (e) { }
                    }
                    setPlanLoaded(true);
                }
            } catch (e) {
                if (mounted) setPlanLoaded(true);
            }
        }

        // Subscription plans belong to tenants, not Super Admin accounts.
        const isSuperAdmin = localStorage.getItem("isSuperAdmin") === "true";
        if (user && !isSuperAdmin) {
            loadPlan();
        } else {
            setSubscriptionPlan(null);
            setPlanLoaded(true);
        }

        return () => { mounted = false; };
    }, [user]);


    // signin: set user and run callback after state update to avoid races
    const signin = (newUser, callback) => {
        // store callback to be executed once `user` state updates
        pendingSigninCallback.current = callback || null;
        setUser(newUser);
    };

    const pendingSigninCallback = useRef(null);

    useEffect(() => {
        if (pendingSigninCallback.current) {
            const cb = pendingSigninCallback.current;
            pendingSigninCallback.current = null;
            try { cb(); } catch (e) { /* ignore callback errors */ }
        }
    }, [user]);

    const signout = async () => {
        try {
            await apiFetch(`/api/account/logout`, { method: "POST", skipTenant: true });
        } catch (e) {
            // ignore
        }
        localStorage.removeItem("user");
        localStorage.removeItem("tenant");
        localStorage.removeItem("isSuperAdmin");
        setUser(null);
    };

    // Listen for global unauthorized events emitted by apiFetch to force logout when token expires
    useEffect(() => {
        const onUnauthorized = (e) => {
            if (unauthorizedHandledRef.current) return;
            unauthorizedHandledRef.current = true;

            try {
                // show user-facing notification explaining why they were logged out
                try { notify('error', { title: 'Session expired', text: 'Your session has expired. Please sign in again.' }, 6000); } catch (ne) { }
            } catch (err) { }
            if (logoutTimerRef.current) {
                try { clearTimeout(logoutTimerRef.current); } catch (timerError) { }
                logoutTimerRef.current = null;
            }
            try { localStorage.removeItem('user'); } catch {}
            try { localStorage.removeItem('tenant'); } catch {}
            try { localStorage.removeItem('isSuperAdmin'); } catch {}
            setUser(null);
            // do not force a full page reload; letting router redirect via ProtectedWrapper is preferred
        };
        window.addEventListener('unauthorized', onUnauthorized);
        return () => window.removeEventListener('unauthorized', onUnauthorized);
    }, [notify]);

    // Keep auth state in sync with other tabs or components that modify localStorage directly
    useEffect(() => {
        const onStorage = (e) => {
            try {
                if (e.key === 'user') {
                    if (!e.newValue) {
                        setUser(null);
                    } else {
                        try { setUser(JSON.parse(e.newValue)); } catch { setUser(null); }
                    }
                }
                if (e.key === 'isSuperAdmin' && !e.newValue) {
                    // ensure we drop superadmin flag when cleared elsewhere
                    try { localStorage.removeItem('isSuperAdmin'); } catch {}
                }
            } catch (err) {
                // ignore
            }
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);

    let value = { user, signin, signout };

    // tenant path prefix can be configured via REACT_APP_TENANT_PREFIX (defaults to 't')
    const tenantPrefix = (process.env.REACT_APP_TENANT_PREFIX || "t").trim();

    if (loader)
        return (
            <div
                style={{
                    flex: 1,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <h1>LOADING...</h1>
            </div>
        );

    return (
        <AuthContext.Provider value={value}>
            <PWAInstallPrompt />
            <BrowserRouter>
                {/* SyncRoute ensures we land on the correct dashboard after signin (avoids route mismatch when storage flags and in-memory user disagree) */}
                <SyncRoute user={user} />
                <Routes>
                    {/* Superadmin dashboard - MUST be first to avoid being caught by /:slug */}
                    <Route
                        path="/superadmin"
                        element={
                            <ProtectedWrapper>
                                <SuperadminDashboard />
                            </ProtectedWrapper>
                        }
                    />
                    <Route
                        path="/superadmin/"
                        element={
                            <ProtectedWrapper>
                                <SuperadminDashboard />
                            </ProtectedWrapper>
                        }
                    />
                    {/* Superadmin sub-routes */}
                    <Route
                        path="/superadmin/tenants"
                        element={
                            <ProtectedWrapper>
                                <SuperadminTenants />
                            </ProtectedWrapper>
                        }
                    />
                    <Route
                        path="/superadmin/analytics"
                        element={
                            <ProtectedWrapper>
                                <SuperadminAnalytics />
                            </ProtectedWrapper>
                        }
                    />
                    <Route
                        path="/superadmin/settings"
                        element={
                            <ProtectedWrapper>
                                <SuperadminSettings />
                            </ProtectedWrapper>
                        }
                    />

                    {/* Account routes (non-tenant and tenant-prefixed) */}
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/verify-email" element={<VerifyEmail />} />
                    <Route path="/reset-password" element={<ResetPassword />} />

                    {/* Tenant-prefixed account routes so /{prefix}/{slug}/verify-email works */}
                    <Route path={`/${tenantPrefix}/:slug/login`} element={<Login />} />
                    <Route path={`/${tenantPrefix}/:slug/register`} element={<Register />} />
                    <Route path={`/${tenantPrefix}/:slug/forgot-password`} element={<ForgotPassword />} />
                    <Route path={`/${tenantPrefix}/:slug/verify-email`} element={<VerifyEmail />} />
                    <Route path={`/${tenantPrefix}/:slug/reset-password`} element={<ResetPassword />} />

                    {/* Tenant-as-first-segment account routes (no /t prefix) e.g. /nonnyplu/login */}
                    <Route path={`/:slug/login`} element={<Login />} />
                    <Route path={`/:slug/register`} element={<Register />} />
                    <Route path={`/:slug/forgot-password`} element={<ForgotPassword />} />
                    <Route path={`/:slug/verify-email`} element={<VerifyEmail />} />
                    <Route path={`/:slug/reset-password`} element={<ResetPassword />} />

                    {/* Public landing page */}
                    <Route path="/" element={<LandingPage />} />

                    {/* Protected area (tenant-prefixed) */}
                    <Route
                        path={`/${tenantPrefix}/:slug`}
                        element={
                            <ProtectedWrapper>
                                <Layout subscriptionPlan={subscriptionPlan} planLoaded={planLoaded} />
                            </ProtectedWrapper>
                        }
                    >
                        <Route index element={<Dashboard />} />
                        <Route path="inventory" element={<Inventory />} />
                        <Route path="purchase-details" element={<PurchaseDetails />} />
                        <Route path="purchase-order-drafts" element={<PurchaseOrderDrafts />} />
                        <Route path="ai-performance" element={<AiPerformance />} />
                        <Route path="returned-products" element={<ReturnedProducts />} />
                        <Route path="sales" element={<Sales />} />
                        <Route path="manage-store" element={<Store />} />
                        <Route path="manage-category" element={<ManageCategory />} />
                        <Route path="manage-users" element={<ManageUsers />} />
                        <Route path="subscription" element={<Subscription />} />
                        <Route path="change-password" element={<ChangePassword />} />
                        <Route path="profile" element={<Profile />} />
                    </Route>

                    {/* Protected area (tenant as first segment, no /t prefix) e.g. /nonnyplus/... */}
                    <Route
                        path={`/:slug`}
                        element={
                            <ProtectedWrapper>
                                <Layout subscriptionPlan={subscriptionPlan} planLoaded={planLoaded} />
                            </ProtectedWrapper>
                        }
                    >
                        <Route index element={<Dashboard />} />
                        <Route path="inventory" element={<Inventory />} />
                        <Route path="purchase-details" element={<PurchaseDetails />} />
                        <Route path="purchase-order-drafts" element={<PurchaseOrderDrafts />} />
                        <Route path="ai-performance" element={<AiPerformance />} />
                        <Route path="returned-products" element={<ReturnedProducts />} />
                        <Route path="sales" element={<Sales />} />
                        <Route path="manage-store" element={<Store />} />
                        <Route path="manage-category" element={<ManageCategory />} />
                        <Route path="manage-users" element={<ManageUsers />} />
                        <Route path="subscription" element={<Subscription />} />
                        <Route path="change-password" element={<ChangePassword />} />
                        <Route path="profile" element={<Profile />} />
                    </Route>

                    <Route path="*" element={<NoPageFound />} />
                </Routes>
            </BrowserRouter>
        </AuthContext.Provider>
    );
};

function SyncRoute({ user }) {
    const navigate = useNavigate();
    useEffect(() => {
        if (!user) return;
        try {
            const isSuper = localStorage.getItem("isSuperAdmin") === "true";
            const tenant = localStorage.getItem("tenant");
            const path = window.location.pathname || "/";
            if (isSuper) {
                if (!path.startsWith("/superadmin")) {
                    navigate("/superadmin", { replace: true });
                }
            } else if (tenant) {
                if (!path.startsWith(`/${tenant}`)) {
                    navigate(`/${tenant}/`, { replace: true });
                }
            }
        } catch (e) {
            // ignore
        }
    }, [user, navigate]);

    return null;
}

export default App;
